import { useMemo, useState } from 'react';

import type { BoatProfile } from '../core/boat';
import {
  type Route,
  estimateRoute,
  isNavigable,
  routeDistance,
  routeLegs,
} from '../core/route';
import type { RouteCheckResult } from '../chart/routeCheck';
import { buildShareUrl, isShareUrlRisky } from '../core/share';
import {
  formatBearing,
  formatDistance,
  formatDuration,
  formatVolume,
  type UnitPreferences,
} from '../core/units';

/**
 * Depth-check result.
 *
 * The wording here is the whole point. A check that could not see every part of
 * the route says so plainly instead of showing a reassuring tick — "nothing
 * found in the water I could see" and "this route is clear" are different
 * claims, and conflating them is how an app gets someone aground.
 */
function RouteCheckSummary({
  check,
  onCheck,
}: {
  check: RouteCheckResult | null;
  onCheck: () => void;
}): React.JSX.Element {
  if (!check) {
    return (
      <button type="button" className="route__check-button" onClick={onCheck}>
        Check route against chart
      </button>
    );
  }

  if (check.warnings.length === 0 && check.complete) {
    return (
      <div className="route__check route__check--clear">
        <strong>No dangers found</strong>
        <span>
          Checked {check.probesChecked} points against charted depths. Chart data only —
          it does not account for tide, and it is not a substitute for a lookout.
        </span>
        <button type="button" onClick={onCheck}>Re-check</button>
      </div>
    );
  }

  if (!check.complete) {
    return (
      <div className="route__check route__check--incomplete" role="alert">
        <strong>Route not fully checked</strong>
        <span>
          {check.probesUnchecked} of {check.probesChecked + check.probesUnchecked} points had no
          chart loaded. Zoom out until the whole route is on screen, then check again.
        </span>
        {check.warnings.length > 0 && (
          <ul className="route__check-list">
            {check.warnings.map((warning, i) => (
              <li key={`${warning.kind}-${i}`} className={`route__check-item route__check-item--${warning.severity}`}>
                {warning.message}
              </li>
            ))}
          </ul>
        )}
        <button type="button" onClick={onCheck}>Re-check</button>
      </div>
    );
  }

  return (
    <div className="route__check route__check--danger" role="alert">
      <strong>
        {check.warnings.length} {check.warnings.length === 1 ? 'danger' : 'dangers'} on this route
      </strong>
      <ul className="route__check-list">
        {check.warnings.map((warning, i) => (
          <li key={`${warning.kind}-${i}`} className={`route__check-item route__check-item--${warning.severity}`}>
            {warning.message}
          </li>
        ))}
      </ul>
      <button type="button" onClick={onCheck}>Re-check</button>
    </div>
  );
}

export interface RoutePanelProps {
  route: Route;
  boat: BoatProfile;
  units: UnitPreferences;
  /** Previously saved routes, most recent first. */
  savedRoutes: readonly Route[];
  /** Result of the last depth check, or null if it has not been run. */
  check: RouteCheckResult | null;
  onCheck: () => void;
  /** Suggest legs that stay in navigable water. Null while none is running. */
  autoRouteStatus: string | null;
  autoRouteBusy: boolean;
  onAutoRoute: () => void;
  onRename: (name: string) => void;
  onUndo: () => void;
  onClear: () => void;
  onReverse: () => void;
  onSave: () => Promise<void> | void;
  onOpen: (route: Route) => void;
  onDelete: (id: string) => Promise<void> | void;
  onClose: () => void;
}

/**
 * Route summary and controls.
 *
 * The numbers are planning estimates from a flat-rate model — constant speed,
 * constant burn — and the panel says so rather than presenting them with a
 * precision the app does not have. Real burn varies with sea state, load,
 * current and fouling.
 */
export function RoutePanel({
  route,
  boat,
  units,
  savedRoutes,
  check,
  onCheck,
  autoRouteStatus,
  autoRouteBusy,
  onAutoRoute,
  onRename,
  onUndo,
  onClear,
  onReverse,
  onSave,
  onOpen,
  onDelete,
  onClose,
}: RoutePanelProps): React.JSX.Element {
  const [shareState, setShareState] = useState<'idle' | 'copied' | 'long' | 'error'>('idle');
  const [saveState, setSaveState] = useState<'idle' | 'saved' | 'error'>('idle');

  const legs = useMemo(() => routeLegs(route), [route]);
  const estimate = useMemo(() => estimateRoute(route, boat), [route, boat]);
  const navigable = isNavigable(route);

  const share = async (): Promise<void> => {
    try {
      const url = await buildShareUrl(route, window.location.href.split('#')[0]);
      if (isShareUrlRisky(url)) {
        setShareState('long');
        return;
      }
      if (navigator.share) {
        await navigator.share({ title: route.name, url });
        setShareState('idle');
        return;
      }
      await navigator.clipboard.writeText(url);
      setShareState('copied');
    } catch {
      setShareState('error');
    }
  };

  const save = async (): Promise<void> => {
    try {
      await onSave();
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  };

  return (
    <div className="sheet sheet--route" role="dialog" aria-label="Route">
      <div className="sheet__header">
        <input
          className="route__name"
          value={route.name}
          onChange={(e) => onRename(e.target.value)}
          aria-label="Route name"
          placeholder="Route name"
        />
        <button type="button" onClick={onClose} aria-label="Close">✕</button>
      </div>

      {!navigable ? (
        <>
          <p className="sheet__hint">
            Tap the chart to drop waypoints. A route needs at least two.
          </p>
          {/* Saved routes are offered only while the current one is empty, so
              opening one can never discard work in progress. */}
          {savedRoutes.length > 0 && (
            <>
              <h3 className="route__saved-heading">Saved routes</h3>
              <ul className="route__saved">
                {savedRoutes.map((saved) => (
                  <li key={saved.id}>
                    <button type="button" className="route__saved-open" onClick={() => onOpen(saved)}>
                      <span className="route__saved-name">{saved.name}</span>
                      <span className="route__saved-meta">
                        {saved.waypoints.length} waypoints ·{' '}
                        {formatDistance(routeDistance(saved), units.distance)}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="route__saved-delete"
                      onClick={() => void onDelete(saved.id)}
                      aria-label={`Delete ${saved.name}`}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      ) : (
        <>
          <dl className="route__summary">
            <div>
              <dt>Distance</dt>
              <dd>{formatDistance(routeDistance(route), units.distance)}</dd>
            </div>
            <div>
              <dt>Time</dt>
              <dd>{formatDuration(estimate.durationS)}</dd>
            </div>
            <div>
              <dt>Fuel</dt>
              <dd>
                {Number.isFinite(estimate.fuelL)
                  ? formatVolume(estimate.fuelL, units.volume)
                  : '—'}
              </dd>
            </div>
          </dl>

          {estimate.exceedsReserve && (
            <p className="route__warning" role="alert">
              This passage uses your fuel reserve.
              {estimate.fuelRemainingL !== undefined && estimate.fuelRemainingL < 0
                && ' It cannot be completed on one tank.'}
            </p>
          )}

          <p className="sheet__hint sheet__hint--small">
            Estimated at {formatDistance(boat.cruiseSpeedMs * 3600, units.distance)}/h cruise and a
            constant burn rate. Planning aid only — not a fuel gauge.
          </p>

          {/* Suggestion, not autopilot: it inserts waypoints the skipper can
              undo, and the depth check below is the thing that vouches for it. */}
          <button
            type="button"
            className="route__check-button route__check-button--secondary"
            onClick={onAutoRoute}
            disabled={autoRouteBusy}
          >
            {autoRouteBusy ? 'Planning…' : 'Suggest route around land'}
          </button>
          {autoRouteStatus && <p className="offline__status">{autoRouteStatus}</p>}

          <RouteCheckSummary check={check} onCheck={onCheck} />

          <ol className="route__legs">
            {legs.map((leg) => (
              <li key={leg.from.id}>
                <span className="route__leg-index">{leg.index + 1}</span>
                <span className="route__leg-bearing">{formatBearing(leg.bearingDeg)}</span>
                <span className="route__leg-distance">
                  {formatDistance(leg.distanceM, units.distance)}
                </span>
              </li>
            ))}
          </ol>
        </>
      )}

      <div className="route__actions">
        <button type="button" onClick={onUndo} disabled={route.waypoints.length === 0}>
          Undo
        </button>
        <button type="button" onClick={onReverse} disabled={!navigable}>
          Reverse
        </button>
        <button type="button" onClick={onClear} disabled={route.waypoints.length === 0}>
          Clear
        </button>
        <button type="button" onClick={save} disabled={!navigable}>
          {saveState === 'saved' ? 'Saved' : 'Save'}
        </button>
        <button type="button" onClick={share} disabled={!navigable}>
          {shareState === 'copied' ? 'Link copied' : 'Share'}
        </button>
      </div>

      {shareState === 'long' && (
        <p className="route__warning" role="alert">
          This route makes a link long enough that some messaging apps will truncate
          it, and a truncated link decodes as a different route. Remove some
          waypoints before sharing.
        </p>
      )}
      {shareState === 'error' && (
        <p className="route__warning" role="alert">Could not create a share link.</p>
      )}
      {saveState === 'error' && (
        <p className="route__warning" role="alert">Could not save this route.</p>
      )}
    </div>
  );
}
